import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem, TextField } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { actions as locationActions } from '../../../../store/ducks/yaLocations.duck';
import AutocompleteLocations from '../../../../components/AutocompleteLocations';
import { thousands } from '../../../../utils/utils';
import { useCartStyles } from '../hooks/useCartStyles';
import { DeliveryType } from '../types';
import { useFormatMessage } from '../../../../hooks';
import {
  selectCart,
  selectCheckoutLoading,
  selectGuestCart,
  selectIsAuthorized,
  selectLoadingByIdYalocaitons,
  selectLoadingYalocations,
  selectMe,
  selectStores,
  selectYalocations,
} from '../selectors';
import { IStore } from '../../../../interfaces/store';
import { ButtonWithLoader } from '../../../../components/other/Buttons';
import TermDialog from '../../../../components/other/Dialog/TermDialog';

export function CartForm({
  formik,
  pickupLocation,
  setPickupLocation,
}: {
  formik: any;
  setPickupLocation: (location: IStore | null) => void;
  pickupLocation: IStore | null;
}) {
  const classes = useCartStyles();
  const fm = useFormatMessage();
  const dispatch = useDispatch();
  const cart = useSelector(selectCart);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [dialogContentType, setDialogContentType] = React.useState('');
  const guestCart = useSelector(selectGuestCart);
  const stores = useSelector(selectStores);
  const locations = useSelector(selectYalocations);
  const loadingLocations = useSelector(selectLoadingYalocations);
  const byCoordsLoading = useSelector(selectLoadingByIdYalocaitons);
  const checkoutLoading = useSelector(selectCheckoutLoading);
  const me = useSelector(selectMe);
  const isAuthorized = useSelector(selectIsAuthorized);

  const disabled = isAuthorized
    ? !me?.company?.name || !me?.company?.inn || !me?.company?.phone_number
    : false;

  const handleDialogClose = () => setOpenDialog(false);

  const handleDialogOpen = useCallback(() => {
    setDialogContentType('public_offer');
    setOpenDialog(true);
  }, [dialogContentType]);

  const { values, errors, touched, handleBlur, handleSubmit, setFieldValue } = formik;

  const cartTotal = useMemo(() => {
    let total = 0;

    if (cart) {
      total =
        values.deliveryType === DeliveryType.self
          ? cart.order_summ
          : cart.order_summ + cart.delivery_cost;
    } else {
      total = guestCart?.order_summ || 0;
    }

    return thousands(String(total.toFixed(2)));
  }, [values.deliveryType, cart, guestCart]);

  const filterPickupStores = useMemo(() => {
    const newStores = stores.reduce<IStore[]>((accumulator, item) => {
      Boolean(item.location?.pickup_point) && accumulator.push(item);
      return accumulator;
    }, []);
    return newStores;
  }, [stores, stores.length]);

  return (
    <>
      <div className={classes.totalCard}>
        <form onSubmit={handleSubmit}>
          <div className={classes.containerForm}>
            <b className={classes.title}>{fm('YOUR.ORDER')}</b>
            <div className={classes.containerValue}>
              <b className={classes.subTitle}>{fm('CART.PRODUCTS')}</b>
              <b className={classes.subTitleValue}>
                {cart?.goods_num || guestCart?.goods_num} штук
              </b>
            </div>
            <div className={classes.containerValue}>
              <b className={classes.subTitle}>{fm('CART.TOTAL')}</b>
              <b className={classes.subTitleValue}>{`${cartTotal} ₽`}</b>
            </div>
          </div>

          {values.deliveryType === DeliveryType.delivery ? (
            <AutocompleteLocations
              options={locations || []}
              loading={loadingLocations || byCoordsLoading}
              inputValue={values.location}
              editable
              label={fm('ADDRESS')}
              inputClassName={classes.autoLoc}
              inputError={Boolean(
                touched.location && errors.location && errors.location.address
              )}
              inputHelperText={touched.location && errors.location && errors.location.address}
              fetchLocations={data => dispatch(locationActions.fetchRequest(data))}
              clearLocations={() => dispatch(locationActions.clear())}
              setSelectedLocation={location => {
                if (location) {
                  setFieldValue('location', location);
                  localStorage.setItem('location', JSON.stringify(location));
                } else {
                  setFieldValue('location', {});
                  localStorage.removeItem('location');
                }
              }}
              handleBlur={handleBlur}
              disable={false}
            />
          ) : (
            <TextField
              select
              type='country'
              label={fm('STORE.INPUT.LOCATION')}
              margin='normal'
              className='kt-width-full'
              variant='outlined'
              value={pickupLocation?.id || undefined}
              onChange={e => {
                const store = filterPickupStores.find(store => store.id === +e.target.value);
                if (store) {
                  setPickupLocation(store);
                  setFieldValue('location', store.location);
                }
              }}
              error={Boolean(touched.location && errors.location && errors.location.address)}
              helperText={touched.location && errors.location && errors.location.address}
            >
              {filterPickupStores.map(item => (
                <MenuItem key={item.id} value={item.id || 0}>
                  {item?.location?.address || 'No address'}
                </MenuItem>
              ))}
            </TextField>
          )}
          <ButtonWithLoader
            marginTop={27}
            fullWidth
            disabled={checkoutLoading || disabled}
            loading={checkoutLoading}
          >
            <FormattedMessage id='CART.CHECKOUT.BUTTON' />
          </ButtonWithLoader>

          <div className={classes.containerOffer}>
            <b className={classes.offerText}>
              Нажимая на кнопку в соглашаетесь с{' '}
              <b className={classes.offerTextBlue} onClick={handleDialogOpen}>
                Публичной офертой
              </b>
            </b>
          </div>
        </form>
      </div>

      <TermDialog
        isOpen={openDialog}
        handleClose={handleDialogClose}
        contentType={dialogContentType}
      />
    </>
  );
}
